import { handle } from 'redux-pack';
import { PhotoSessionDetail } from 'models/PhotoSessionDetail';
import { logUncaught } from 'utils/log';

export const buildPhotoSessionDetailFailureError = (payload = {}) => {
  const httpStatus = payload.response && payload.response.status;

  if (typeof httpStatus === 'number') {
    return new Error(`[Failure fetching photo session details][http:${httpStatus}]`);
  }

  if (payload.name && payload.message) {
    return new Error(
      `[Failure fetching photo session details][${payload.name}: ${payload.message}]`,
    );
  }

  return new Error('[Failure fetching photo session details][unknown]');
};

export const buildPhotoSessionDetailFailureContext = (prevState, payload = {}) => {
  const httpStatus = payload.response && payload.response.status;

  return {
    state: prevState,
    payload,
    failureType:
      typeof httpStatus === 'number'
        ? 'http'
        : payload.name || 'unknown',
    httpStatus: typeof httpStatus === 'number' ? httpStatus : undefined,
    errorName: payload.name,
    errorMessage: payload.message,
  };
};

export const handlePhotoSessionDetail = (prevState, action, error) => {
  if (error) {
    const statusCodes =
      action.payload.response && action.payload.response.status;

    logUncaught(
      buildPhotoSessionDetailFailureError(action.payload),
      buildPhotoSessionDetailFailureContext(prevState, action.payload),
    );

    return statusCodes === 403
      ? prevState
      : {
        ...prevState,
        showError: true,
      };
  }

  const newPhotoSessionDetail = new PhotoSessionDetail(action.payload.job);

  return {
    ...newPhotoSessionDetail,
    entryAccessData: prevState.entryAccessData,
    listingRepresentative: prevState.listingRepresentative,
    listingRepresentativeLoaded: prevState.listingRepresentativeLoaded,
  };
};

export const handlePhotoSessionDetailLifecycle = (action) => ({
  success: (prevState) => handlePhotoSessionDetail(prevState, action, false),
  failure: (prevState) => handlePhotoSessionDetail(prevState, action, true),
});

export const addPhotoSessionDetailToState = (state, action) =>
  handle(state, action, handlePhotoSessionDetailLifecycle(action));

export const handleStartPhotoSession = (prevState, action) => {
  const status = action.payload.job && action.payload.job.status;

  const state = prevState;
  if (status) {
    state.status = status;
  }
  return state;
};

export const handleStartPhotoSessionLifecycle = (action) => ({
  success: (prevState) => handleStartPhotoSession(prevState, action),
});

export const startPhotoSessionInStore = (state, action) =>
  handle(state, action, handleStartPhotoSessionLifecycle(action));

export const handleEntryAccessSuccess = (prevState, action) => ({
  ...prevState,
  entryAccessData: action.payload,
});

export const handleEntryAccessFailure = (prevState, action) => {
  logUncaught(new Error('[Failure fetching entry access]'), {
    state: prevState,
    payload: action.payload,
  });

  return prevState;
};

export const handleEntryAccessLifecycle = (action) => ({
  success: (prevState) => handleEntryAccessSuccess(prevState, action),
  failure: (prevState) => handleEntryAccessFailure(prevState, action),
});

export const handleEntryAccess = (state, action) =>
  handle(state, action, handleEntryAccessLifecycle(action));

export const handleListingRepresentativeSuccess = (prevState, action) => ({
  ...prevState,
  listingRepresentative: action.payload,
  listingRepresentativeLoaded: true,
});

export const handleListingRepresentativeFailure = (prevState) => ({
  ...prevState,
  listingRepresentative: null,
  listingRepresentativeLoaded: true,
});

export const handleListingRepresentativeLifecycle = (action) => ({
  success: (prevState) => handleListingRepresentativeSuccess(prevState, action),
  failure: (prevState) => handleListingRepresentativeFailure(prevState),
});

export const handleListingRepresentative = (state, action) =>
  handle(state, action, handleListingRepresentativeLifecycle(action));
